import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    FaRegBell,
    FaSearch,
    FaInbox,
    FaBullhorn,
    FaHistory,
    FaTimes,
    FaExclamationTriangle,
    FaInfoCircle,
    FaCheckCircle,
    FaTimesCircle,
    FaCheckDouble
} from 'react-icons/fa';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import '../../Student/views/StudentNotifications.css'; // Reusing existing notification styles

const SuperAdminNotifications = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [activeTab, setActiveTab] = useState('alerts'); // 'alerts', 'system', 'history'
    const [searchTerm, setSearchTerm] = useState('');

    const handleNotifClick = (notif) => {
        const title = notif.title?.toLowerCase() || '';
        const message = notif.message?.toLowerCase() || '';

        if (title.includes('course') || message.includes('course')) {
            navigate('/admin/courses');
        } else if (title.includes('job') || message.includes('job')) {
            navigate('/admin/jobs');
        } else if (title.includes('ticket') || title.includes('support') || message.includes('ticket') || message.includes('support')) {
            navigate('/admin/support');
        } else if (title.includes('payment') || title.includes('invoice') || message.includes('payment') || message.includes('invoice')) {
            navigate('/admin/payments');
        }

        // Also mark as read when clicked
        markAsRead(notif._id);
    };

    useEffect(() => {
        fetchNotifications();
    }, []);

    const fetchNotifications = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/notifications', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(res.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching admin notifications", err);
            setLoading(false);
        }
    };

    const markAsRead = async (id) => {
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`/api/notifications/${id}/read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const updated = notifications.filter(n => n._id !== id);
            setNotifications(updated);

            // Sync with header count
            const newUnread = updated.filter(n => !n.isRead).length;
            window.dispatchEvent(new CustomEvent('notificationsRead', { detail: { count: newUnread } }));
        } catch (err) {
            showError("Action Failed", "Could not dismiss notification.");
        }
    };

    const markAllRead = async () => {
        try {
            showLoading("Clearing all notifications...");
            const token = localStorage.getItem('token');
            await axios.patch('/api/notifications/mark-all-read', {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications([]);

            // Sync with header count
            window.dispatchEvent(new CustomEvent('notificationsRead', { detail: { count: 0 } }));

            closeLoading();
            showSuccess("Success", "All notifications dismissed.");
        } catch (err) {
            closeLoading();
            showError("Action Failed", "Could not clear hub.");
        }
    };

    const filteredNotifications = notifications.filter(n => {
        const matchesSearch = n.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            n.message?.toLowerCase().includes(searchTerm.toLowerCase());

        if (activeTab === 'alerts') return matchesSearch && (n.type === 'Alert');
        if (activeTab === 'system') return matchesSearch && (n.type !== 'Alert');
        return matchesSearch;
    });

    const getIcon = (type) => {
        switch (type) {
            case 'Alert': return <FaExclamationTriangle className="type-icon alert" />;
            case 'Success': return <FaCheckCircle className="type-icon success" />;
            case 'Warning': return <FaTimesCircle className="type-icon warning" />;
            default: return <FaInfoCircle className="type-icon info" />;
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="student-notifications fade-in">
            <div className="hub-header">
                <div className="header-info">
                    <h1 className="hub-title"><FaRegBell /> Admin Command Center</h1>
                    <p className="hub-subtitle">Critical platform updates, moderation alerts, and system-wide signals.</p>
                </div>
                <div className="header-actions">
                    <button className="clear-all-btn" onClick={markAllRead} disabled={notifications.length === 0}>
                        <FaCheckDouble /> Mark All as Read
                    </button>
                </div>
            </div>

            <div className="hub-controls">
                <div className="tab-filters">
                    <button
                        className={`tab-btn ${activeTab === 'alerts' ? 'active' : ''}`}
                        onClick={() => setActiveTab('alerts')}
                    >
                        <FaBullhorn /> Priority Alerts
                        {notifications.filter(n => n.type === 'Alert').length > 0 && (
                            <span className="tab-badge">{notifications.filter(n => n.type === 'Alert').length}</span>
                        )}
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'system' ? 'active' : ''}`}
                        onClick={() => setActiveTab('system')}
                    >
                        <FaInbox /> System Signals
                        {notifications.filter(n => n.type !== 'Alert').length > 0 && (
                            <span className="tab-badge">{notifications.filter(n => n.type !== 'Alert').length}</span>
                        )}
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        <FaHistory /> Archived Updates
                    </button>
                </div>

                <div className="search-bar">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search command log..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="notifications-list">
                {filteredNotifications.length > 0 ? (
                    filteredNotifications.map((notif) => (
                        <div
                            key={notif._id}
                            className={`notif-card ${notif.type.toLowerCase()} interactive`}
                            onClick={() => handleNotifClick(notif)}
                            style={{
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                borderLeft: '4px solid transparent'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateX(5px)';
                                e.currentTarget.style.backgroundColor = '#F8FAFC';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateX(0)';
                                e.currentTarget.style.backgroundColor = '';
                            }}
                        >
                            <div className="notif-icon-box">
                                {getIcon(notif.type)}
                            </div>
                            <div className="notif-content">
                                <div className="notif-meta">
                                    <span className={`notif-badge ${notif.type.toLowerCase()}`}>{notif.type}</span>
                                    <span className="notif-date">{new Date(notif.createdAt).toLocaleString()}</span>
                                </div>
                                <h3 className="notif-title">{notif.title}</h3>
                                <p className="notif-message">{notif.message}</p>
                            </div>
                            <button
                                className="dismiss-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    markAsRead(notif._id);
                                }}
                                title="Clear Notification"
                            >
                                <FaTimes />
                            </button>
                        </div>
                    ))
                ) : (
                    <div className="empty-hub">
                        <div className="empty-icon-wrapper">
                            <FaRegBell />
                        </div>
                        <h3>Command Center Clear</h3>
                        <p>No new administrative directives at this time.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SuperAdminNotifications;
